import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Paper,
  IconButton,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Close } from '@mui/icons-material';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import SmsIcon from '@mui/icons-material/Sms';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import WarningIcon from '@mui/icons-material/Warning';
import { AxiosError } from 'axios';
import { logger } from '../utils/logger';
import apiClient from '../utils/api';
import SMSMFASetup from './SMSMFASetup';
import TOTPSetup from './TOTPSetup';
import { MFAVerification } from './MFAVerification';
import { useSensitiveOperation } from '../hooks/useSensitiveOperation';
import { featureFlags } from '../utils/featureFlags';
import { extractApiErrorMessage } from '../utils/errorUtils';

interface ApiErrorResponse {
  detail?: unknown;
}

interface MFAStatus {
  totpEnabled: boolean;
  smsEnabled: boolean;
  phoneNumber?: string;
  phoneNumberVerified?: boolean;
}

const MFAManagement: React.FC = () => {
  const theme = useTheme();
  const fullScreenMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [mfaStatus, setMfaStatus] = useState<MFAStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showSmsSetup, setShowSmsSetup] = useState(false);
  const [showTotpSetup, setShowTotpSetup] = useState(false);
  const [disablingSms, setDisablingSms] = useState(false);
  const [disablingTotp, setDisablingTotp] = useState(false);
  const [showDisableConfirm, setShowDisableConfirm] = useState<'totp' | 'sms' | null>(null);
  const [pendingDisableAction, setPendingDisableAction] = useState<'totp' | 'sms' | null>(null);
  const [mfaVerifyError, setMfaVerifyError] = useState<string | null>(null);
  const [mfaVerifying, setMfaVerifying] = useState(false);

  const {
    executeSensitiveOperation,
    isMfaRequired,
    resetMfaRequired,
    isExecuting: sensitiveOpExecuting,
  } = useSensitiveOperation();

  useEffect(() => {
    fetchMFAStatus();
  }, []);

  const fetchMFAStatus = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await apiClient.get('/api/mfa/status');
      setMfaStatus(response.data);
    } catch (err) {
      logger.error('Failed to fetch MFA status', err);
      setError('Failed to load MFA settings. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleTotpSetupComplete = async () => {
    setShowTotpSetup(false);
    await fetchMFAStatus();
  };

  const handleSmsSetupComplete = async () => {
    setShowSmsSetup(false);
    await fetchMFAStatus();
  };

  const executeDisableTotp = useCallback(async () => {
    setDisablingTotp(true);
    try {
      await apiClient.delete('/api/mfa/totp/disable');
      await fetchMFAStatus();
    } catch (err) {
      logger.error('Failed to disable TOTP MFA', err);
      const axiosError = err as AxiosError<ApiErrorResponse>;
      // Check if it's an MFA required error
      if (axiosError.response?.status === 403) {
        // The executeSensitiveOperation will handle this
        throw err;
      }
      setError(extractApiErrorMessage(axiosError.response?.data, axiosError.response?.status ?? 0) || 'Failed to disable authenticator app');
    } finally {
      setDisablingTotp(false);
      setPendingDisableAction(null);
    }
  }, []);

  const handleDisableTotp = async () => {
    setShowDisableConfirm(null);
    setPendingDisableAction('totp');

    try {
      await executeSensitiveOperation(executeDisableTotp);
    } catch (error) {
      // Error is already handled
      logger.debug('TOTP disable failed', error);
    }
  };

  const executeDisableSms = useCallback(async () => {
    setDisablingSms(true);
    try {
      await apiClient.delete('/api/sms-mfa/disable');
      await fetchMFAStatus();
    } catch (err) {
      logger.error('Failed to disable SMS MFA', err);
      const axiosError = err as AxiosError<ApiErrorResponse>;
      // Check if it's an MFA required error
      if (axiosError.response?.status === 403) {
        // The executeSensitiveOperation will handle this
        throw err;
      }
      setError(extractApiErrorMessage(axiosError.response?.data, axiosError.response?.status ?? 0) || 'Failed to disable SMS MFA');
    } finally {
      setDisablingSms(false);
      setPendingDisableAction(null);
    }
  }, []);

  const handleDisableSms = async () => {
    if (!mfaStatus) return;

    // Check if this is the last MFA method
    if (!mfaStatus.totpEnabled) {
      setError('Cannot disable SMS MFA. It is your only MFA method. Please add another method first.');
      return;
    }

    setShowDisableConfirm(null);
    setPendingDisableAction('sms');

    try {
      await executeSensitiveOperation(executeDisableSms);
    } catch (error) {
      // Error is already handled
      logger.debug('SMS MFA disable failed', error);
    }
  };

  const handleMfaVerified = async (_code: string) => {
    setMfaVerifying(true);
    setMfaVerifyError(null);

    try {
      resetMfaRequired();

      // Retry the pending action after MFA verification
      if (pendingDisableAction === 'totp') {
        await executeDisableTotp();
      } else if (pendingDisableAction === 'sms') {
        await executeDisableSms();
      }
    } catch (err) {
      logger.error('MFA verification failed', err);
      setMfaVerifyError('Verification failed. Please try again.');
    } finally {
      setMfaVerifying(false);
    }
  };

  const handleMfaCancel = () => {
    resetMfaRequired();
    setPendingDisableAction(null);
  };

  const canDisableTotp = (): boolean => {
    if (!mfaStatus) return false;
    return mfaStatus.smsEnabled;
  };

  const canDisableSms = (): boolean => {
    if (!mfaStatus) return false;
    return mfaStatus.totpEnabled;
  };

  // Count total enabled MFA methods
  const getEnabledMfaCount = (): number => {
    if (!mfaStatus) return 0;
    let count = 0;
    if (mfaStatus.totpEnabled) count++;
    if (mfaStatus.smsEnabled) count++;
    return count;
  };

  return (
    <Box>
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      )}

      {!loading && error && !mfaStatus && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {!loading && mfaStatus && (
        <>
      <Typography variant="body2" paragraph color="text.secondary">
        Manage your multi-factor authentication methods.
        {getEnabledMfaCount() > 0 && (
          <Chip
            label={`${getEnabledMfaCount()} method${getEnabledMfaCount() !== 1 ? 's' : ''} enabled`}
            color="success"
            size="small"
            sx={{ ml: 1 }}
          />
        )}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* TOTP Status */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <PhoneAndroidIcon sx={{ mr: 1, color: mfaStatus?.totpEnabled ? 'success.main' : 'text.secondary' }} />
            <Typography variant="subtitle1">Authenticator App (TOTP)</Typography>
          </Box>
          {!mfaStatus?.totpEnabled && (
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              size="small"
              onClick={() => setShowTotpSetup(true)}
            >
              Enable Authenticator
            </Button>
          )}
        </Box>

        {mfaStatus?.totpEnabled ? (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1 }}>
              <Typography variant="body2" color="text.secondary">
                <Chip label="Enabled" color="success" size="small" sx={{ mr: 1 }} />
                Using authenticator app for MFA
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="outlined"
                  color="primary"
                  size="small"
                  onClick={() => setShowTotpSetup(true)}
                  startIcon={<RefreshIcon />}
                >
                  Reset
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  onClick={() => setShowDisableConfirm('totp')}
                  disabled={!canDisableTotp() || disablingTotp || sensitiveOpExecuting}
                  startIcon={disablingTotp ? <CircularProgress size={16} /> : <DeleteIcon />}
                >
                  {disablingTotp ? 'Disabling...' : 'Disable'}
                </Button>
              </Box>
            </Box>
            {!canDisableTotp() && (
              <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
                Cannot disable authenticator app. It is your only MFA method. Enable SMS MFA first.
              </Typography>
            )}
          </>
        ) : (
          <Typography variant="body2" color="text.secondary">
            <Chip label="Not configured" size="small" sx={{ mr: 1 }} />
            Use an authenticator app like Google Authenticator or Authy for secure verification codes
          </Typography>
        )}
      </Paper>

      {/* SMS MFA Status - only shown when SMS MFA feature is enabled */}
      {featureFlags.isEnabled('enableSmsMfa') && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <SmsIcon sx={{ mr: 1, color: mfaStatus?.smsEnabled ? 'warning.main' : 'text.secondary' }} />
              <Typography variant="subtitle1">SMS Text Message</Typography>
            </Box>
            {!mfaStatus?.smsEnabled && (
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                size="small"
                onClick={() => setShowSmsSetup(true)}
              >
                Enable SMS MFA
              </Button>
            )}
          </Box>

          {mfaStatus?.smsEnabled ? (
            <>
              <Alert severity="warning" sx={{ mb: 2 }} icon={<WarningIcon />}>
                <Typography variant="body2">
                  SMS authentication is less secure than authenticator app due to SIM swapping risks.
                </Typography>
              </Alert>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">
                  <Chip label="Enabled" color="warning" size="small" sx={{ mr: 1 }} />
                  Phone: {mfaStatus.phoneNumber || 'Configured'}
                </Typography>
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  onClick={() => setShowDisableConfirm('sms')}
                  disabled={!canDisableSms() || disablingSms || sensitiveOpExecuting}
                  startIcon={disablingSms ? <CircularProgress size={16} /> : <DeleteIcon />}
                >
                  {disablingSms ? 'Disabling...' : 'Disable SMS MFA'}
                </Button>
              </Box>
              {!canDisableSms() && (
                <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
                  Cannot disable SMS MFA. It is your only MFA method. Enable authenticator app first.
                </Typography>
              )}
            </>
          ) : (
            <Typography variant="body2" color="text.secondary">
              <Chip label="Not configured" size="small" sx={{ mr: 1 }} />
              Receive verification codes via text message (less secure than authenticator app)
            </Typography>
          )}
        </Paper>
      )}

        </>
      )}

      {/* TOTP Setup Dialog */}
      <Dialog open={showTotpSetup} onClose={() => setShowTotpSetup(false)} maxWidth="sm" fullWidth fullScreen={fullScreenMobile}>
        <DialogTitle>
          Set Up Authenticator App
          <IconButton
            aria-label="close"
            onClick={() => setShowTotpSetup(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <TOTPSetup
            onComplete={handleTotpSetupComplete}
            onCancel={() => setShowTotpSetup(false)}
          />
        </DialogContent>
      </Dialog>

      {/* SMS Setup Dialog - only shown when SMS MFA feature is enabled */}
      {featureFlags.isEnabled('enableSmsMfa') && (
        <Dialog open={showSmsSetup} onClose={() => setShowSmsSetup(false)} maxWidth="sm" fullWidth fullScreen={fullScreenMobile}>
          <DialogTitle>
            Set Up SMS MFA
            <IconButton
              aria-label="close"
              onClick={() => setShowSmsSetup(false)}
              sx={{ position: 'absolute', right: 8, top: 8 }}
            >
              <Close />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <SMSMFASetup
              onComplete={handleSmsSetupComplete}
              onCancel={() => setShowSmsSetup(false)}
            />
          </DialogContent>
        </Dialog>
      )}

      {/* Disable Confirmation Dialog */}
      <Dialog
        open={showDisableConfirm !== null}
        onClose={() => setShowDisableConfirm(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Confirm Disable {showDisableConfirm === 'totp' ? 'Authenticator App' : 'SMS MFA'}
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2">
              {showDisableConfirm === 'totp'
                ? 'Are you sure you want to disable your authenticator app? You will need to set it up again if you want to use it in the future.'
                : 'Are you sure you want to disable SMS MFA? You will no longer receive verification codes via text message.'}
            </Typography>
          </Alert>
          <Typography variant="body2" color="text.secondary">
            For security, you may be asked to verify your identity before this action can be completed.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDisableConfirm(null)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={showDisableConfirm === 'totp' ? handleDisableTotp : handleDisableSms}
          >
            Disable
          </Button>
        </DialogActions>
      </Dialog>

      {/* MFA Re-verification Dialog */}
      <MFAVerification
        open={isMfaRequired}
        onVerify={handleMfaVerified}
        onCancel={handleMfaCancel}
        loading={mfaVerifying}
        error={mfaVerifyError}
        mfaMethod={mfaStatus?.totpEnabled ? 'totp' : mfaStatus?.smsEnabled ? 'sms' : undefined}
      />
    </Box>
  );
};

export default MFAManagement;
